import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MapPin, BedDouble, Bath, Ruler, Star, BadgeCheck, Check, Share2, Eye, Flame, Clock } from "lucide-react";
import { IMG } from "@/lib/api";

const formatPrice = (n, isRent) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}${isRent ? "/mo" : ""}`;
};

// "Posted N days ago" — parses ISO created_at and returns a short human label.
function _timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function PropertyCard({ property, isFav, onToggleFav }) {
  const isRent = property.listing_type === "rent";
  const featured = !!property.is_featured;
  const img = property.image_paths?.[0]
    ? IMG(property.image_paths[0])
    : "https://images.pexels.com/photos/16110999/pexels-photo-16110999.jpeg?auto=compress&cs=tinysrgb&h=650&w=940";

  // Activity signals — trust-builders shown on every card.
  const views = property.view_count || 0;
  const contacts = property.contact_count || 0;
  // Trending = many enquiries OR high views. Loose thresholds so ~15-20%
  // of listings light up as "hot" (matches real-market intuition).
  const isTrending = contacts >= 20 || views >= 350;
  const timeAgo = _timeAgo(property.created_at);
  // "N tenants viewing now" — playful live-activity chip. We derive a small
  // pseudo-live number from total views (~2% show up simultaneously) so the
  // chip feels real without needing a websocket. Only shown for higher-view
  // listings — spamming everywhere destroys the effect.
  const viewersNow = views >= 80 ? Math.max(2, Math.min(28, Math.round(views * 0.025))) : 0;

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/property/${property.id}`;
    const headline = `${property.bhk} BHK ${property.property_type} ${isRent ? "for rent" : "for sale"} in ${property.locality || property.city}`;
    const msg = `🏠 ${headline}\n💰 ${formatPrice(property.price, isRent)}\n📍 ${property.locality ? property.locality + ", " : ""}${property.city}\n\nContact owner directly (zero brokerage) 👇\n${url}\n\nvia To-Let Rentals`;
    if (navigator.share) {
      try { await navigator.share({ title: headline, text: msg, url }); return; } catch {}
    }
    // Fallback → WhatsApp web
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    toast.success("Opening WhatsApp…");
  };

  return (
    <div
      className={`hover-lift bg-white rounded-2xl overflow-hidden border relative ${
        featured
          ? "border-[#C9A24A]/70 ring-1 ring-[#C9A24A]/50 shadow-[0_10px_36px_-8px_rgba(201,162,74,0.45)]"
          : "border-brand-subtle"
      }`}
      data-testid="property-card"
    >
      {featured && (
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#E7C566] via-[#C9A24A] to-[#A87F2E] z-10" />
      )}
      <Link to={`/property/${property.id}`} className="block">
        <div className="relative overflow-hidden aspect-[4/3]">
          <img src={img} alt={property.title} className="img-zoom w-full h-full object-cover" />
          <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase ${
              isRent ? "bg-[#FF5A3D] text-white" : "bg-[#0EA5E9] text-white"
            }`}>
              {isRent ? "For Rent" : "For Sale"}
            </span>
            {isTrending && (
              <span
                data-testid="trending-badge"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-gradient-to-r from-[#FF6B35] to-[#C9302C] text-white shadow-md"
              >
                <Flame size={11} className="fill-white" /> Trending
              </span>
            )}
            {property.is_featured && (
              <span data-testid="featured-badge" className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase bg-gradient-to-r from-[#E7C566] via-[#C9A24A] to-[#A87F2E] text-white shadow-md ring-1 ring-white/40">
                <Star size={11} className="fill-white" /> Featured
              </span>
            )}
          </div>
          {viewersNow > 0 && (
            <span
              data-testid="viewers-now-chip"
              className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-black/70 backdrop-blur text-white"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#22C55E] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22C55E]" />
              </span>
              {viewersNow} viewing now
            </span>
          )}
          {onToggleFav && (
            <button
              data-testid="favorite-button"
              onClick={(e) => { e.preventDefault(); onToggleFav(property.id); }}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white"
              aria-label="Save property"
            >
              <Heart size={18} className={isFav ? "fill-[#0EA5E9] text-[#0EA5E9] pop" : "text-gray-700"} />
            </button>
          )}
          <button
            data-testid="card-share-btn"
            onClick={share}
            className={`absolute ${onToggleFav ? "top-3 right-16" : "top-3 right-3"} w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors`}
            aria-label="Share property"
            title="Share on WhatsApp"
          >
            <Share2 size={17} className="text-[#25D366]" />
          </button>
        </div>
        <div className="p-5">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <div className="font-display text-2xl font-semibold brand-forest">
              {formatPrice(property.price, isRent)}
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{property.property_type}</span>
          </div>
          <div className="flex items-center gap-1.5 mb-1">
            <h3 className="font-body font-semibold text-gray-900 line-clamp-1">{property.title}</h3>
            {property.owner_verified && (
              <BadgeCheck size={16} className="text-emerald-600 shrink-0" data-testid="card-verified-tick" aria-label="Verified owner" />
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
            <MapPin size={14} /> <span className="line-clamp-1">{property.locality}, {property.city}</span>
          </div>
          <div className="inline-flex items-center gap-1 mb-3 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest border border-emerald-100">
            <Check size={10} /> Zero brokerage
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-700 border-t border-brand-subtle pt-3">
            <span className="flex items-center gap-1"><BedDouble size={15} /> {property.bhk} BHK</span>
            <span className="flex items-center gap-1"><Bath size={15} /> {property.bathrooms}</span>
            <span className="flex items-center gap-1"><Ruler size={15} /> {property.area_sqft} sqft</span>
          </div>
          {(views > 0 || contacts > 0 || timeAgo) && (
            <div
              data-testid="activity-strip"
              className="flex items-center gap-3 mt-3 pt-3 border-t border-brand-subtle text-[11px] text-gray-500"
            >
              {views > 0 && (
                <span className="inline-flex items-center gap-1"><Eye size={11} /> {views.toLocaleString("en-IN")} views</span>
              )}
              {contacts > 0 && (
                <span className="inline-flex items-center gap-1 text-[#7A1E2A] font-semibold">
                  <span className="w-1 h-1 rounded-full bg-[#7A1E2A]" />
                  {contacts} enquir{contacts === 1 ? "y" : "ies"}
                </span>
              )}
              {timeAgo && (
                <span className="inline-flex items-center gap-1 ml-auto"><Clock size={11} /> {timeAgo}</span>
              )}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}
